import { SubmitStore } from "Stores/RESTStore/SubmitStore";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { observable, ObservableMap, action, computed, makeObservable, runInAction } from "mobx";
import { RcFile, UploadFile, UploadFileStatus } from "antd/lib/upload/interface";
import { API_URL } from "General/APIEndpoints";
import { FileUploadChunked } from "Pages/Project/Documents/FileUpload/FileUploadChunked";

export interface EmailUploadFile extends UploadFile {
  // when "uid" prop is the one that is generated at the client, "id" refers to file that is stored at the back-end
  id?: string | number;
  accessLevel?: API_FILEATTACHMENT_ACCESSLEVEL;
  viewers?: API_FileDataEntity["viewers"];
  fileActions?: API_FileDataEntity["fileActions"];
  linkId?: string;
  fileDataEntity?: API_FileDataEntity;
}
type EmailSubmitMessageType = "success" | "subject" | "okLabel" | "cancelLabel" | "error";
type EmailSubmitMessageByAction = Record<EmailSubmitAction, { [key in EmailSubmitMessageType]: string }>;
export type EmailSubmitAction = "sent" | "save_draft" | "edit_draft" | "copy_draft";
export const submitMessageMap: EmailSubmitMessageByAction = {
  sent: {
    success: "Email was sent",
    subject: "Send this email without subject",
    okLabel: "Send anyway",
    cancelLabel: "Do not send",
    error: "Unable to send email",
  },
  save_draft: {
    success: "Draft was saved",
    subject: "Save this draft without subject",
    okLabel: "Save anyway",
    cancelLabel: "Do not save",
    error: "Unable to save draft",
  },
  edit_draft: {
    success: "Draft was edited",
    subject: "Edit this draft without subject",
    okLabel: "Edit anyway",
    cancelLabel: "Do not edit",
    error: "Unable to edit draft",
  },
  copy_draft: {
    success: "Draft was copied",
    subject: "Copy this draft without subject",
    okLabel: "Copy anyway",
    cancelLabel: "Do not copy",
    error: "Unable to copy draft",
  },
};

export interface MailInitialRecipients {
  initialRecipients?: Pick<API_MailResponse, "to" | "cc" | "bcc">;
}

/**
 * Interface for response that is received when face an error on sending project files.
 * NO_ACCESS - some of the users do not have access to some of the attached files.
 * PIN_REQUIRED - have to set up temporary access for external users/emails.
 * SHARING_FORBIDDEN - current user does not have access to share some of the files.
 */

class EmailSubmitStore extends SubmitStore<API_MailRequest> {
  filesMap: ObservableMap<string, EmailUploadFile> = new Map() as ObservableMap<string, EmailUploadFile>;

  constructor() {
    super();

    makeObservable(this, {
      filesMap: observable,
      clearAll: action.bound,
      submitAll: action.bound,
      fileList: computed,
      getFileIds: action.bound,
      deleteFile: action.bound,
      addExternalFile: action.bound,
      addProjectFile: action.bound,
      removeAttachments: action.bound,
      editDraft: action.bound,
      copyDraft: action.bound,
      externalFilesLoading: computed,
    });

    this.filesMap = new Map() as ObservableMap;
  }

  url(): string {
    return API_URL.MAILS.send(projectsDataStore.projectId);
  }

  clearAll() {
    this.clear();
    if (this.filesMap) {
      this.filesMap.clear();
    } else {
      this.filesMap = new Map() as ObservableMap;
    }
  }

  async submitAll(action: EmailSubmitAction = "sent") {
    try {
      // add attachments id's to email request
      const attachments = this.getFileIds();
      this.data.attachments = attachments;
      // Because "from" field makes back-end crash on sending email - we extract it
      const combinedFormData = { ...this.data, attachments };
      const { from: _, ...formDataWithoutFrom } = combinedFormData;
      // Check for draft "edit" action
      if (action === "edit_draft") {
        await this.editDraft(formDataWithoutFrom);
      } else {
        await super.submit(formDataWithoutFrom, false);
      }
    } catch (err) {
      throw err;
    }
  }

  async copyDraft(id: string, type: API_MAILRESPONSE_TYPE) {
    try {
      await API.newData<API_BooleanData>(API_URL.MAILS.cloneDraft(projectsDataStore.projectId, type, id), {});
    } catch (error) {
      throw error;
    }
  }

  async editDraft(formData: API_MailRequest) {
    try {
      const response = await API.editData<API_MailRequest>(
        API_URL.MAILS.editDraft(projectsDataStore.projectId, formData.type),
        formData
      );
      runInAction(() => {
        this.data = response;
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Methods for handling file attachments
   */

  fileUrl(id?: string) {
    if (id) {
      return API_URL.FILE_UPLOAD_V3.deleteTemporaryFile(projectsDataStore.projectId, id);
    } else {
      return API_URL.FILE_UPLOAD_V3.uploadTemporaryFile(projectsDataStore.projectId);
    }
  }

  get fileList(): EmailUploadFile[] {
    return Array.from(this.filesMap.values());
  }

  getFileIds(): string[] {
    // TODO: handle issue when getting files that are not finished uploading?
    const fileIds = this.filesMap.keys();
    return Array.from(fileIds);
  }

  private findKeyByUid(map: ObservableMap<string, EmailUploadFile>, uid: string) {
    const entry = Array.from(map).find(([, value]) => value.uid === uid);
    return entry ? entry[0] : null;
  }

  async deleteFile(uid: string) {
    try {
      if (typeof uid === "number") {
        uid = `@id${uid}`;
      }
      uid = this.findKeyByUid(this.filesMap, uid);
      const file = this.filesMap.get(uid);
      if (!file) {
        throw new Error(`No file found for ID: ${uid}`);
      }
      this.filesMap.delete(uid);
      if (file.status === "done") {
        // if file belongs to externally attached - delete from temp database
        await API.deleteData(API_URL.FILE_UPLOAD_V3.deleteTemporaryFile(projectsDataStore.projectId, file.id));
      }
    } catch (err) {
      throw err;
    }
  }

  /**
   * Handles adding of files through drag-n-drop component of antd library.
   * "@temp-" suffix used to mark externally loaded files on attaching to email in order to recognize them at back-end.
   * @param file - object that is returned by antd/Upload.Dragger component.
   */
  addExternalFile(file: EmailUploadFile) {
    if (!FileUploadChunked.fileIsNotTooBig(file, true)) {
      return;
    }
    if (!file.id && file.status === "done") {
      // if file has just finished upload
      const response: API_TemporaryFile[] = file.response;
      if (Array.isArray(response)) {
        if (response.length === 1) {
          // if received only one file
          // tslint:disable-next-line:no-any
          const uid = `@temp-${response[0].id}`; // implicitly use backend file id as a uid value
          const splitFileName = response[0].name.split(".");
          this.filesMap.delete(`${file.uid}`);
          file.uid = uid;
          file.id = response[0].id;
          file.name = response[0].name;
          file.type = splitFileName[splitFileName.length - 1];
          // file.expiration = response[0].expiration; FEATURE: you can add "expiration" prop here
          this.filesMap.set(`${file.uid}`, file);
        } else {
          // back-end extracts zip archive, so we add them as files one by one
          response.forEach((responseFile) => {
            // tslint:disable-next-line:no-any
            const uid = `@temp-${responseFile.id}`; // implicitly use backend file id as a uid value
            const splitFileName = responseFile.name.split(".");

            this.filesMap.set(uid, {
              uid: uid,
              originFileObj: {} as RcFile,
              status: "done" as UploadFileStatus,
              id: responseFile.id,
              size: responseFile.sizeBytes,
              name: responseFile.name,
              type: splitFileName[splitFileName.length - 1], // TODO: add mimeType in back-end response
            });

            this.filesMap.delete(`${file.uid}`); // remove archive from attaches list
          });
        }
      }
    } else {
      // if file is uploading or has already processed
      this.filesMap.set(`${file.uid}`, file);
    }
  }

  addProjectFile(entity: API_FileDataEntity) {
    this.filesMap.set(entity.linkId ? `@link${entity.linkId}` : `@id${entity.id}`, {
      uid: `@id${entity.id}`,
      originFileObj: {} as RcFile,
      fileDataEntity: Object.assign({}, entity),
      id: entity.id,
      size: entity.size,
      name: entity.name,
      type: entity.type,
      viewers: entity.viewers,
      fileActions: entity.fileActions,
      ...(entity.linkId && { linkId: `@link${entity.linkId}` }),
    });
  }

  removeAttachments() {
    this.filesMap.clear();
    this.data.attachments = [];
  }

  get externalFilesLoading() {
    return this.fileList.some((file) => file.status && file.status !== "done");
  }
}

const emailSubmitStore = new EmailSubmitStore();
export { emailSubmitStore, EmailSubmitStore };
